import type { ReactNode } from 'react'

import { Card } from '../ui'

/**
 * One line of the digest: a finished sentence and nothing to compute.
 *
 * `body` arrives written. Every count, date and flag in it was read and counted
 * in `pages/product/Home.tsx`, which `clientScience.test.ts` scans site by site;
 * this component has never seen a recording or a baseline, so it cannot invent
 * a measurement (D6 of the 1.9.1 Home brief, `boundary.test.ts`).
 */
export interface DigestLine {
  /** A stable React key — what the line is about, never its position. */
  key: string
  /**
   * Amber marks a baseline that is held low, and nothing else. The words say it
   * too, so the tone is never the only channel (WCAG 1.4.1).
   */
  tone?: 'amber'
  body: ReactNode
}

/**
 * "At a glance": what one page of recordings and two baseline reads can back.
 *
 * ── FACTS, NEVER A VERDICT (AC3, R37) ───────────────────────────────────────
 *
 * Each line is a count, a date or a payload flag. There is no streak — a run of
 * days is a fact, a streak is a thing someone can break — no praise, and no line
 * that grades the person. A line whose count is zero, or whose read failed, is
 * left out by the caller rather than stated.
 *
 * ── ALWAYS MOUNTED ONCE THE BODY PAINTS ─────────────────────────────────────
 *
 * The card renders in every state except first run, even with no lines,
 * because it carries the screen's one "Record a session" link (D5 of the 1.9.1
 * brief). With no lines it is that action alone: no eyebrow over an empty list,
 * and no sentence written to fill the space.
 */
export function HomeDigest({ lines, action }: { lines: readonly DigestLine[]; action?: ReactNode }) {
  const hasLines = lines.length !== 0
  return (
    <Card eyebrow={hasLines ? 'At a glance' : undefined} className="home-digest" data-testid="home-digest">
      {hasLines && (
        <ul className="digest">
          {lines.map((line) => (
            <li key={line.key}>
              <i
                className={line.tone === 'amber' ? 'digest__dot digest__dot--amber' : 'digest__dot'}
                aria-hidden="true"
              />
              <span>{line.body}</span>
            </li>
          ))}
        </ul>
      )}
      {/* Sprint 1.9.9 D6: under the mirror the one Record action is the top
          section's, so the digest is given none and draws no empty row. */}
      {action != null && <div className="digest__actions">{action}</div>}
    </Card>
  )
}
